import { getActivePinia } from 'pinia';
import { unref } from 'vue';

import englishCanada from '../messages/en-CA/shell.json' with { type: 'json' };
import frenchCanada from '../messages/fr-CA/shell.json' with { type: 'json' };

// The words the shell says for itself: the labels of its own doors, and the
// names a screen reader gives its two components.
//
// They ship with the package, in every language the apps ship, so an app that
// places the shell has nothing to translate. An application that wants a
// different word defines the same key in its own vue-i18n messages, and its
// word wins.

const DEFAULT_LOCALE = 'en-CA';

const OWN_WORDS = {
    'en-CA': englishCanada,
    'fr-CA': frenchCanada
};

// The package's bundle for a locale: the exact one, or the one that shares its
// language (`fr` reads `fr-CA`), or the default.
function bundleFor(locale) {
    if (OWN_WORDS[locale]) {
        return OWN_WORDS[locale];
    }
    const language = String(locale).split('-')[0];
    const sameLanguage = Object.keys(OWN_WORDS).find((candidate) => candidate.split('-')[0] === language);
    if (sameLanguage) {
        return OWN_WORDS[sameLanguage];
    }
    return OWN_WORDS[DEFAULT_LOCALE];
}

function lookUp(bundle, key) {
    let value = bundle;
    for (const part of key.split('.')) {
        value = value?.[part];
    }
    return value;
}

/**
 * One word, for the application's current language.
 *
 * `host` is the application's vue-i18n (its `global`), or null when it has none.
 * The application's own message at this key wins; otherwise the package's word
 * in the application's language; otherwise the package's default language.
 */
function resolveWord(host, key) {
    if (host && host.te(key)) {
        return host.t(key);
    }
    let locale = DEFAULT_LOCALE;
    if (host) {
        locale = unref(host.locale);
    }
    return lookUp(bundleFor(locale), key) ?? lookUp(OWN_WORDS[DEFAULT_LOCALE], key);
}

// The application's vue-i18n, as the three things this needs from it, or null
// when the application has none.
//
// It is read off the application's global properties — `$i18n`, `$t`, `$te` —
// which vue-i18n puts there for every application that installs it. vue-i18n
// does not provide itself under a key a package can `inject`: its own injection
// key is private to each instance.
function hostOf(application) {
    const globals = application?.config.globalProperties;
    if (!globals?.$i18n || typeof globals.$t !== 'function' || typeof globals.$te !== 'function') {
        return null;
    }
    return {
        te: (key) => globals.$te(key),
        t: (key) => globals.$t(key),
        get locale() {
            return globals.$i18n.locale;
        }
    };
}

/**
 * A `word(key)` function bound to the application's vue-i18n, found once, where
 * this is called: a component's setup, or a store's. Outside an application —
 * a unit test — it speaks the default language.
 *
 * The application is reached through the Pinia it installed (`_a`, the one
 * Pinia property this leans on that Pinia marks internal). That is the same in
 * a component and in a store, and it does not depend on which of them happens
 * to ask first.
 */
function useShellWords() {
    const host = hostOf(getActivePinia()?._a);
    return (key) => resolveWord(host, key);
}

export { resolveWord, useShellWords };
